import { isNil } from 'lodash'
import { Button, Grid, styled, Typography } from 'opensolar-ui'
import { useMemo, useState } from 'react'

import { ComponentLikeType, SellerIntegrationType } from 'opensolar-common'
import { useTranslate } from 'ra-core'
import { useHistory } from 'react-router-dom'
import { UserType } from '../../userUtils'
import { FormatterUtils } from '../../utils'
import DistributorChips from './DistributorChips'
import ImagePlaceholder from './ImagePlaceholder'
import PercentageDiscountTag from './PercentageDiscountTag'

interface ProductCardProps<T> {
  component: T
  userType: UserType
  enabledSellerIntegrations?: SellerIntegrationType[]
  onProductClick?: (componentCode: string) => void
}

const Container = styled('div')(() => ({
  display: 'block',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '16px 16px 24px',
  gap: '16px',
  width: '230px',
  height: '320px',
  left: '0px',
  top: '0px',
  background: '#FFFFFF',
  borderRadius: '8px',
  border: '1px solid rgb(0, 0, 0, 0.2)',
}))

const ProductImage = styled('img', { name: 'ProductImage' })(() => ({
  width: '100%',
  height: '160px',
  objectFit: 'contain',
  borderRadius: '8px',
}))

const Section = styled(Grid)(() => ({
  height: '80px',
}))

const PriceTag = styled('div', { name: 'PriceTag' })(() => ({
  margin: '5px 0px',
  fontSize: '18px',
}))

const ShortDescription = styled('div', { name: 'ShortDescription' })(() => ({
  height: '100%',
  '& p': {
    display: '-webkit-box',
    WebkitLineClamp: '2',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
    margin: 0,
    wordBreak: 'break-word',
  },
}))

const DiscountTag = styled('div', { name: 'DiscountTag' })(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 10,
}))

const ViewProductButton = styled(Button, { name: 'ViewProductButton' })(() => ({
  background: '#4272DD',
  borderRadius: '6px',
  fontSize: '15px',
  width: '100%',
  padding: '0 10px',
  minHeight: '40px',
  justifyContent: 'center',
  color: 'white',
  '&:hover': {
    border: 'none',
    background: '#113B98',
  },
}))
const PriceAndButtonContainer = styled(Grid, { name: 'PriceAndButtonContainer' })(() => ({
  width: '100%',
}))
const GridContainer = styled(Grid, { name: 'GridContainer' })(() => ({
  marginBottom: '5px',
}))
const ImageContainer = styled('div', { name: 'ImageContainer' })(() => ({
  position: 'relative',
  width: '100%',
}))

const ProductTile = ({
  component,
  userType,
  enabledSellerIntegrations,
  onProductClick,
}: ProductCardProps<ComponentLikeType>) => {
  const history = useHistory()
  const translate = useTranslate()
  const [isImageLinkBroken, setIsImageBroken] = useState(false)
  const currencySymbol = useMemo(() => {
    return component?.ordering_v2.find((order) => !!order.currency_symbol)?.currency_symbol ?? ''
  }, [component])

  if (isNil(component)) return null

  // Set the default order which has the discount value
  const defaultOrder = component.ordering_v2.find((orderingV2) => orderingV2.price_detail.discount_value)
  const discountPercentValue = defaultOrder?.price_detail.discount_value || undefined
  const imageImageUrl =
    defaultOrder && defaultOrder.image_url
      ? defaultOrder.image_url
      : (component.ordering_v2.find((orderingV2) => orderingV2.image_url)?.image_url ?? '')
  const shortDescription =
    defaultOrder && defaultOrder.short_description
      ? defaultOrder.short_description
      : (component.ordering_v2.find((orderingV2) => orderingV2.short_description)?.short_description ?? '')
  const price =
    defaultOrder && defaultOrder.price
      ? defaultOrder.price
      : (component.ordering_v2.find((orderingV2) => orderingV2.price)?.price ?? 0)

  const handleClick = () => {
    onProductClick?.(component.code)
    history.push('/shop/product/' + encodeURIComponent(component.code))
  }

  const BuyFrom = ({ align }) => {
    return (
      <>
        <Typography textVariant="helper1" colorHex="#757575" align={align}>
          {translate('BUY FROM')}
        </Typography>
        <Grid container justify={align === 'right' ? 'flex-end' : 'flex-start'}>
          <DistributorChips ordering_v2={component.ordering_v2} enabledSellerIntegrations={enabledSellerIntegrations} />
        </Grid>
      </>
    )
  }

  const Price = (
    <>
      <PriceTag>
        {currencySymbol}
        {FormatterUtils.formatCurrencyNumber(price, 2)}
      </PriceTag>
    </>
  )

  return (
    <Container>
      <Grid container direction="column" justify="space-between" alignItems="center" spacing={2}>
        <GridContainer item container>
          {!isImageLinkBroken && imageImageUrl ? (
            <ImageContainer>
              {discountPercentValue && (
                <DiscountTag>
                  <PercentageDiscountTag
                    showUpTo={false}
                    percentage={FormatterUtils.formatPercentageValue(discountPercentValue)}
                  />
                </DiscountTag>
              )}
              <ProductImage src={imageImageUrl} alt="Product" onError={() => console.log(imageImageUrl)} />
            </ImageContainer>
          ) : (
            <ImagePlaceholder />
          )}
        </GridContainer>

        <Section item container justify="space-evenly" alignItems="flex-start" alignContent="center" direction="row">
          <Grid item xs={12} sm={userType === UserType.PRO ? 8 : 12}>
            <ShortDescription>
              <Typography textVariant="helper1" colorHex="#757575" noWrap>
                {component.code}
              </Typography>
              <Typography textVariant="body1">{shortDescription}</Typography>
            </ShortDescription>
          </Grid>

          <Grid item xs={12} sm={4}>
            {userType === UserType.PRO ? <BuyFrom align="right" /> : null}
          </Grid>
        </Section>

        <Section item container direction="row" alignItems="stretch">
          <PriceAndButtonContainer item>
            {userType === UserType.PRO ? Price : <BuyFrom align="left" />}
          </PriceAndButtonContainer>
          <PriceAndButtonContainer item>
            <ViewProductButton onClick={handleClick}> {translate('View Product')} </ViewProductButton>
          </PriceAndButtonContainer>
        </Section>
      </Grid>
    </Container>
  )
}

export default ProductTile
